import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import type { ZapierIntegration } from "@app/analytics/types/zapierTypes";
import apiClient from "@app/common/api/apiClient";
import useAuth from "@app/common/auth/useAuth";

const EMPTY_API_KEYS: ZapierIntegration[] = [];

const useZapier = ({ enabled }: { enabled: boolean } = { enabled: true }) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const queryKey = ["integrations", "zapier", user?.id];

  const invalidateIntegrations = () => {
    queryClient.invalidateQueries({ queryKey });
    queryClient.invalidateQueries({ queryKey: ["integrations"] });
  };

  const listQuery = useQuery({
    queryKey,
    queryFn: apiClient.integrations.zapier.listApiKeys,
    enabled,
  });

  const regenerateApiKey = useMutation({
    mutationFn: apiClient.integrations.zapier.regenerateApiKey,
    onSettled: invalidateIntegrations,
  });

  const createApiKey = useMutation({
    mutationFn: apiClient.integrations.zapier.createApiKey,
    onSettled: invalidateIntegrations,
  });

  const deleteApiKey = useMutation({
    mutationFn: apiClient.integrations.zapier.deleteApiKey,
    onSettled: invalidateIntegrations,
  });

  const sendTestRequest = useMutation({
    mutationFn: apiClient.integrations.zapier.sendTestRequest,
  });

  return {
    isLoading: listQuery.isLoading,
    isError: listQuery.isError,
    apiKeys: listQuery.data ?? EMPTY_API_KEYS,

    regenerateApiKey,
    createApiKey,
    deleteApiKey,
    sendTestRequest,
  };
};

export default useZapier;
